import { PREORDER_ACTIVE, PREORDER_DISPATCH_LABEL } from "@/lib/storeStatus";
import { getVisitorGeoFn } from "@/lib/geo/visitor-geo.functions";
import { useEffect, useState } from "react";
import { Truck, BadgeIndianRupee, CreditCard, Check, Loader2 } from "lucide-react";

type EtaResult = {
  pincode: string;
  city: string;
  etaLabel: string;
  cod: boolean;
};

const METRO_PREFIXES: Record<string, string> = {
  "110": "Delhi NCR",
  "400": "Mumbai",
  "560": "Bengaluru",
  "600": "Chennai",
  "700": "Kolkata",
  "500": "Hyderabad",
  "411": "Pune",
  "122": "Gurugram",
  "201": "Noida / Ghaziabad",
  "380": "Ahmedabad",
};

function lookupPincode(pincode: string): EtaResult | null {
  if (!/^\d{6}$/.test(pincode)) return null;
  const prefix = pincode.slice(0, 3);
  const metro = METRO_PREFIXES[prefix];
  if (metro) {
    return {
      pincode,
      city: metro,
      etaLabel: PREORDER_ACTIVE ? `Dispatches ${PREORDER_DISPATCH_LABEL} · 2-4 days after dispatch` : "Delivered in 2-4 days",
      cod: !PREORDER_ACTIVE,
    };
  }
  // Tier-2/3 — COD still supported under ₹5000.
  return {
    pincode,
    city: "Serviceable",
    etaLabel: PREORDER_ACTIVE ? `Dispatches ${PREORDER_DISPATCH_LABEL} · 2-4 days after dispatch` : "Delivered in 2-4 days",
    cod: !PREORDER_ACTIVE,
  };
}

const STORAGE_KEY = "truwe.deliveryPincode";

export function PincodeDeliveryBar() {
  const [value, setValue] = useState("");
  const [result, setResult] = useState<EtaResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [geoCity, setGeoCity] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getVisitorGeoFn()
      .then((g) => {
        if (alive && g.country === "IN" && g.city) setGeoCity(g.city);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const cached = JSON.parse(raw) as EtaResult;
        setResult(cached);
        setValue(cached.pincode);
      }
    } catch {
      /* ignore */
    }
  }, []);

  function check() {
    const clean = value.replace(/\D/g, "").slice(0, 6);
    if (clean.length !== 6) return;
    setChecking(true);
    // Simulate a quick lookup so the UX feels real.
    setTimeout(() => {
      const r = lookupPincode(clean);
      if (r) {
        setResult(r);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(r));
        } catch {
          /* ignore */
        }
      }
      setChecking(false);
    }, 350);
  }

  function reset() {
    setResult(null);
    setValue("");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="mt-5 rounded-2xl border border-border bg-card p-3.5">
      <div className="flex items-center gap-2">
        <Truck className="h-4 w-4 flex-shrink-0 text-brand" />
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-deep">
          Delivery & payment
        </p>
      </div>

      {result ? (
        <div className="mt-2.5 animate-fade-in">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[13px] font-semibold text-brand-deep">
                {result.etaLabel}{" "}
                <span className="font-normal text-muted-foreground">to {result.city}</span>
              </p>
              <p className="text-[11px] text-muted-foreground">PIN {result.pincode}</p>
            </div>
            <button
              type="button"
              onClick={reset}
              className="text-[11px] font-semibold text-brand underline-offset-2 hover:underline"
            >
              Change
            </button>
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {result.cod && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200">
                <BadgeIndianRupee className="h-3 w-3" /> COD available
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-brand-deep ring-1 ring-brand/15">
              <CreditCard className="h-3 w-3" /> EMI & Pay-in-3
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-foreground ring-1 ring-border">
              <Check className="h-3 w-3 text-brand" /> Free shipping
            </span>
          </div>
        </div>
      ) : (
        <>
        {geoCity && (
          <p className="mt-2 text-[13px] font-semibold text-brand-deep">
            {PREORDER_ACTIVE ? "Fast delivery" : "Delivered in 2-4 days"}{" "}
            <span className="font-normal text-muted-foreground">to {geoCity}</span>
          </p>
        )}
        <form
          className="mt-2.5 flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            check();
          }}
        >
          <input
            type="tel"
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter pincode"
            maxLength={6}
            aria-label="Delivery pincode"
            className="min-w-0 flex-1 rounded-xl border border-border bg-white px-3 py-2.5 text-sm tabular-nums text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
          <button
            type="submit"
            disabled={value.length !== 6 || checking}
            className="inline-flex h-10 items-center justify-center rounded-xl bg-foreground px-4 text-xs font-semibold uppercase tracking-wider text-background transition active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Check"}
          </button>
        </form>
        </>
      )}
    </div>
  );
}
